import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Input,
  SpinnerLoader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Label,
  DataTable,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  X,
  Search,
  AlertTriangle,
} from 'lucide-react';
import apiClient from '@lift-admin/utils/apiClient';
import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import { useAuthContext } from '@lift-admin/utils/provider';
import { Toastify } from 'lift-ui';

interface EngenException {
  id: number;
  engen_id: string;
  taxonomy_type: string;
  created_at: string;
  updated_at: string;
}

interface CreateExceptionData {
  engen_id: string;
  taxonomy_type: string;
}

interface UpdateExceptionData {
  engen_id?: string;
  taxonomy_type?: string;
}

const TAXONOMY_TYPES = [
  'campaign',
  'adset',
  'ad'
];

const EngenExceptions: React.FC = () => {
  const { canEdit, canManage } = useAuthContext();
  const hasEditAccess = canEdit('taxonomy');
  const hasManageAccess = canManage('taxonomy');

  const [exceptions, setExceptions] = useState<EngenException[]>([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [openCreateSheet, setOpenCreateSheet] = useState(false);
  const [openEditSheet, setOpenEditSheet] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedException, setSelectedException] = useState<EngenException | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [usingClientPagination, setUsingClientPagination] = useState(false);

  // Filters
  const [taxonomyTypeFilter, setTaxonomyTypeFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [updateWithCascade, setUpdateWithCascade] = useState(true);

  // Form data
  const [createForm, setCreateForm] = useState<CreateExceptionData>({
    engen_id: '',
    taxonomy_type: '',
  });

  const [editForm, setEditForm] = useState<UpdateExceptionData>({
    engen_id: '',
    taxonomy_type: '',
  });

  // Memoized form handlers to prevent unnecessary re-renders
  const handleCreateFormChange = React.useCallback((field: keyof CreateExceptionData, value: string) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEditFormChange = React.useCallback((field: keyof UpdateExceptionData, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // Debounce function to prevent rapid API calls
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  const fetchExceptions = React.useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = `/txnmy/api/v1/engen-exceptions`;
      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        base_types_only: showAllTypes ? false : true,
      };

      // Use search endpoint if search query is provided
      if (searchQuery.trim()) {
        endpoint = `/txnmy/api/v1/engen-exceptions/search`;
        params.q = searchQuery.trim();
        params.cascade = false;
      }

      if (taxonomyTypeFilter) {
        params.taxonomy_type = taxonomyTypeFilter;
        if (searchQuery.trim()) {
          params.cascade = true;
        }
      }

      const response = await apiClient.get(endpoint, { params });
      const data = response.data;

      // Handle different response formats
      if (data.items && typeof data.total === 'number') {
        const sortedItems = [...data.items].sort((a, b) => a.id - b.id);
        setExceptions(sortedItems);
        setTotalCount(data.total);
      } else if (Array.isArray(data)) {
        if (data.length > rowsPerPage) {
          const sortedData = [...data].sort((a, b) => a.id - b.id);
          const startIndex = page * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          const paginatedItems = sortedData.slice(startIndex, endIndex);
          setExceptions(paginatedItems);
          setTotalCount(data.length);
          setUsingClientPagination(true);
        } else {
          const sortedData = [...data].sort((a, b) => a.id - b.id);
          setExceptions(sortedData);
          setUsingClientPagination(false);

          let countEndpoint = `/txnmy/api/v1/engen-exceptions/count`;
          let countParams: any = {
            base_types_only: showAllTypes ? false : true,
          };

          if (searchQuery.trim()) {
            countEndpoint = `/txnmy/api/v1/engen-exceptions/search/count`;
            countParams.q = searchQuery.trim();
            countParams.cascade = false;
          }

          if (taxonomyTypeFilter) {
            countParams.taxonomy_type = taxonomyTypeFilter;
            if (searchQuery.trim()) {
              countParams.cascade = true;
            }
          }

          const countResponse = await apiClient.get(countEndpoint, { params: countParams });
          const countData = countResponse.data;
          if (countData.count !== undefined) {
            setTotalCount(countData.count);
          } else if (typeof countData === 'number') {
            setTotalCount(countData);
          } else if (countData.total !== undefined) {
            setTotalCount(countData.total);
          }
        }
      } else {
        setExceptions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching exceptions:', error);
      Toastify('error', 'Failed to fetch exceptions');
      setExceptions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, taxonomyTypeFilter, searchQuery, showAllTypes]);

  // Reset to first page when filter changes
  useEffect(() => {
    setPage(0);
  }, [taxonomyTypeFilter, searchQuery]);

  // Debounced search function
  const debouncedSearch = React.useMemo(
    () => debounce((query: string) => {
      setIsSearching(true);
      setSearchQuery(query);
    }, 500),
    []
  );

  // Fetch data when page, rowsPerPage, filter, or showAllTypes changes
  useEffect(() => {
    fetchExceptions();
  }, [page, rowsPerPage, taxonomyTypeFilter, showAllTypes]);

  // Handle search with debouncing
  useEffect(() => {
    if (searchQuery !== undefined) {
      fetchExceptions();
    }
  }, [searchQuery]);

  // Reset searching state when fetch completes
  useEffect(() => {
    if (!loading) {
      setIsSearching(false);
    }
  }, [loading]);

  const handleCreateException = async () => {
    setCreateLoading(true);
    try {
      await apiClient.post('/txnmy/api/v1/engen-exceptions', createForm);
      await logAdminAction(
        `Engen exception created (Engen ID ${createForm.engen_id}, ${createForm.taxonomy_type})`,
        {
          url: '/txnmy/api/v1/engen-exceptions',
          method: 'POST',
          status: 200,
          category: 'taxonomy',
          action: 'create_engen_exception',
          details: [
            { label: 'Engen ID', value: createForm.engen_id },
            { label: 'Taxonomy Type', value: createForm.taxonomy_type },
          ],
        }
      );
      setCreateForm({ engen_id: '', taxonomy_type: '' });
      setOpenCreateSheet(false);
      Toastify('success', 'Exception created successfully');
      setPage(0);
      fetchExceptions();
    } catch (error: any) {
      console.error('Error creating exception:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create exception';
      Toastify('error', errorMessage);
      await logAdminAction(
        `Failed to create engen exception (Engen ID ${createForm.engen_id}): ${errorMessage}`,
        { url: '/txnmy/api/v1/engen-exceptions', method: 'POST', category: 'taxonomy', action: 'create_engen_exception' },
        true
      );
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateException = async () => {
    if (!selectedException) {
      console.error('No exception selected for update');
      Toastify('error', 'No exception selected for update');
      return;
    }

    try {
      const updateData: UpdateExceptionData = {};
      if (editForm.engen_id !== selectedException.engen_id) {
        updateData.engen_id = editForm.engen_id;
      }
      if (editForm.taxonomy_type !== selectedException.taxonomy_type) {
        updateData.taxonomy_type = editForm.taxonomy_type;
      }

      if (Object.keys(updateData).length === 0) {
        setOpenEditSheet(false);
        return;
      }

      const url = `/txnmy/api/v1/engen-exceptions/${selectedException.id}`;
      const params = updateWithCascade ? { cascade: true } : {};

      await apiClient.put(url, updateData, { params });

      const changes = [];
      if (
        updateData.engen_id !== undefined &&
        String(updateData.engen_id) !== String(selectedException.engen_id)
      ) {
        changes.push({
          label: 'Engen ID',
          from: String(selectedException.engen_id),
          to: String(updateData.engen_id),
        });
      }
      if (
        updateData.taxonomy_type !== undefined &&
        updateData.taxonomy_type !== selectedException.taxonomy_type
      ) {
        changes.push({
          label: 'Taxonomy Type',
          from: String(selectedException.taxonomy_type),
          to: String(updateData.taxonomy_type),
        });
      }
      const changeSummary = changes.map(c => `${c.label}: ${c.from} → ${c.to}`).join('; ');
      await logAdminAction(
        `Engen exception #${selectedException.id} updated${
          changeSummary ? ` — ${changeSummary}` : ''
        }${updateWithCascade ? ' (with cascade)' : ''}`,
        {
          url,
          method: 'PUT',
          status: 200,
          category: 'taxonomy',
          action: 'update_engen_exception',
          exceptionId: selectedException.id,
          cascade: updateWithCascade,
          changes,
          details: [{ label: 'Exception ID', value: String(selectedException.id) }],
        }
      );

      setOpenEditSheet(false);
      setSelectedException(null);
      setUpdateWithCascade(true);
      const message = updateWithCascade
        ? 'Exception updated successfully with cascading to related types'
        : 'Exception updated successfully';
      Toastify('success', message);
      fetchExceptions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to update exception';
      Toastify('error', errorMessage);
      await logAdminAction(
        `Failed to update engen exception #${selectedException.id}: ${errorMessage}`,
        { url: `/txnmy/api/v1/engen-exceptions/${selectedException.id}`, method: 'PUT', category: 'taxonomy', action: 'update_engen_exception' },
        true
      );
    }
  };

  const handleDeleteException = async () => {
    if (!selectedException) return;

    try {
      const params = {
        taxonomy_type: selectedException.taxonomy_type,
        cascade: true
      };

      await apiClient.delete(`/txnmy/api/v1/engen-exceptions/by-engen-id/${selectedException.engen_id}`, { params });

      await logAdminAction(
        `Engen exception deleted (Engen ID ${selectedException.engen_id}, ${selectedException.taxonomy_type}) with cascade`,
        {
          url: `/txnmy/api/v1/engen-exceptions/by-engen-id/${selectedException.engen_id}`,
          method: 'DELETE',
          status: 200,
          category: 'taxonomy',
          action: 'delete_engen_exception',
          exceptionId: selectedException.id,
          details: [
            { label: 'Engen ID', value: selectedException.engen_id },
            { label: 'Taxonomy Type', value: selectedException.taxonomy_type },
          ],
        }
      );

      setOpenDeleteDialog(false);
      setSelectedException(null);
      Toastify('success', 'Exception deleted successfully with cascading');
      fetchExceptions();
    } catch (error: any) {
      console.error('Error deleting exception:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete exception';
      Toastify('error', errorMessage);
      await logAdminAction(
        `Failed to delete engen exception (Engen ID ${selectedException.engen_id}): ${errorMessage}`,
        { url: `/txnmy/api/v1/engen-exceptions/by-engen-id/${selectedException.engen_id}`, method: 'DELETE', category: 'taxonomy', action: 'delete_engen_exception' },
        true
      );
    }
  };

  const handleEditClick = (exception: EngenException) => {
    setSelectedException(exception);
    setEditForm({
      engen_id: exception.engen_id,
      taxonomy_type: exception.taxonomy_type,
    });
    setUpdateWithCascade(true);
    setOpenEditSheet(true);
  };

  const handleDeleteClick = (exception: EngenException) => {
    setSelectedException(exception);
    setOpenDeleteDialog(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const dataTableColumns = useMemo((): TableColumn<EngenException>[] => {
    const cols: TableColumn<EngenException>[] = [
      { key: 'id', header: 'ID', accessor: (row) => row.id, width: '80px' },
      { key: 'engen_id', header: 'Engen ID', accessor: (row) => row.engen_id, width: '20%', render: (_, row) => <span className="font-medium">{row.engen_id}</span> },
      { key: 'taxonomy_type', header: 'Taxonomy Type', accessor: (row) => row.taxonomy_type, width: '16%', render: (_, row) => <Badge variant="default">{row.taxonomy_type}</Badge> },
      { key: 'created_at', header: 'Created At', accessor: (row) => row.created_at, width: '20%', render: (_, row) => <span className="text-muted-foreground">{formatDate(row.created_at)}</span> },
      { key: 'updated_at', header: 'Updated At', accessor: (row) => row.updated_at, width: '20%', render: (_, row) => <span className="text-muted-foreground">{formatDate(row.updated_at)}</span> },
    ];
    if (hasEditAccess || hasManageAccess) {
      cols.push({
        key: 'actions',
        header: 'Actions',
        accessor: () => '',
        width: '120px',
        align: 'center',
        render: (_, row) => (
          <div className="flex items-center justify-center gap-1">
            {hasEditAccess && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handleEditClick(row)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
            )}
            {hasManageAccess && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteClick(row)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            )}
          </div>
        ),
      });
    }
    return cols;
  }, [hasEditAccess, hasManageAccess]);

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full overflow-hidden bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-4 border-b bg-white gap-4">
          <h1 className="text-xl font-semibold text-gray-900">Engen Exceptions</h1>
          <div className="flex items-center gap-3 flex-1 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchExceptions}
              disabled={loading}
              className="flex items-center gap-2 h-8 px-3"
            >
              {loading ? (
                <SpinnerLoader className="h-4 w-4" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
            <Select
              value={taxonomyTypeFilter || "all"}
              onValueChange={(value) => setTaxonomyTypeFilter(value === "all" ? "" : value)}
            >
              <SelectTrigger className="w-[180px] h-8">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {TAXONOMY_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasEditAccess && (
              <Button size="sm" className="flex items-center gap-2 h-8 px-3" onClick={() => setOpenCreateSheet(true)}>
                <Plus className="h-4 w-4" />
                Create New
              </Button>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchInputValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInputValue(value);
                  debouncedSearch(value);
                }}
                className="pl-10 pr-8 w-64"
              />
              {searchInputValue && (
                <button
                  onClick={() => {
                    setSearchInputValue('');
                    setSearchQuery('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0 px-8 pt-4 pb-4">

          {/* Search Status Indicator */}
        {(searchQuery || showAllTypes) && (
            <div className="mb-4 p-3 bg-muted/50 rounded-lg border flex-shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
              {searchQuery && (
                  <div className="flex items-center gap-2">
                  {isSearching ? (
                      <SpinnerLoader className="h-4 w-4" />
                  ) : (
                      <Search className="h-4 w-4 text-muted-foreground" />
                  )}
                    <span className="text-sm text-muted-foreground">
                    {isSearching ? 'Searching' : 'Search results'} for: <strong>"{searchQuery}"</strong>
                    {!isSearching && ` • Found ${totalCount} result${totalCount !== 1 ? 's' : ''}`}
                    </span>
                  </div>
              )}
              {showAllTypes && (
                  <span className="text-sm text-muted-foreground">
                    Showing <strong>all types</strong> (including p_campaign, p_adset, p_ad)
                  </span>
              )}
              </div>
            </div>
        )}

        {/* Table - flex-1 min-h-0 so it fills remaining space (same pattern as Adverity Logs) */}
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 flex flex-col min-h-0 rounded-lg shadow-sm overflow-hidden bg-white">
              <DataTable<EngenException>
                className="flex-1 min-h-0 overflow-auto"
                maxHeight="calc(100vh - 160px)"
                columns={dataTableColumns}
                data={exceptions}
                loading={loading}
                emptyMessage="No exceptions found"
                stickyHeader
                stickyFooter
                showActions={false}
                pagination={{
                  currentPage: page + 1,
                  totalPages: Math.max(1, totalPages),
                  pageSize: rowsPerPage,
                  totalItems: totalCount,
                  onPageChange: (p) => setPage(p - 1),
                  onPageSizeChange: (size) => {
                    setRowsPerPage(size);
                    setPage(0);
                  },
                  pageSizeOptions: [50, 100, 500, 1000],
                }}
              />
            </div>
          </div>
        </div>

        {/* Create Sheet */}
        <Sheet open={openCreateSheet} onOpenChange={setOpenCreateSheet}>
          <SheetContent
            side="right"
            className="w-[400px] sm:max-w-[400px] flex flex-col h-full"
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>Create New Exception</SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto py-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="create-engen-id">Engen ID *</Label>
                  <Input
                    id="create-engen-id"
              value={createForm.engen_id}
              onChange={(e) => handleCreateFormChange('engen_id', e.target.value)}
                    placeholder="Enter engen ID"
            />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-taxonomy-type">Taxonomy Type *</Label>
              <Select
                value={createForm.taxonomy_type}
                    onValueChange={(value) => handleCreateFormChange('taxonomy_type', value)}
                  >
                    <SelectTrigger id="create-taxonomy-type" className="w-full">
                      <SelectValue placeholder="Select taxonomy type" />
                    </SelectTrigger>
                    <SelectContent>
                {TAXONOMY_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                    {type}
                        </SelectItem>
                ))}
                    </SelectContent>
              </Select>
                </div>
              </div>
            </div>
            <SheetFooter className="border-t pt-4">
            <Button
                variant="outline"
              onClick={() => {
                  setOpenCreateSheet(false);
                setCreateForm({ engen_id: '', taxonomy_type: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateException}
              disabled={!createForm.engen_id || !createForm.taxonomy_type || createLoading}
            >
                {createLoading ? <SpinnerLoader className="h-4 w-4 mr-2" /> : null}
                Create
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Edit Sheet */}
        <Sheet open={openEditSheet} onOpenChange={setOpenEditSheet}>
          <SheetContent
            side="right"
            className="w-[400px] sm:max-w-[400px] flex flex-col h-full"
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>
            Edit Exception {selectedException && `(ID: ${selectedException.id})`}
              </SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto py-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-engen-id">Engen ID *</Label>
                  <Input
                    id="edit-engen-id"
                    value={editForm.engen_id || ''}
              onChange={(e) => handleEditFormChange('engen_id', e.target.value)}
                    placeholder="Enter engen ID"
            />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-taxonomy-type">Taxonomy Type *</Label>
              <Select
                    value={editForm.taxonomy_type || ''}
                    onValueChange={(value) => handleEditFormChange('taxonomy_type', value)}
                  >
                    <SelectTrigger id="edit-taxonomy-type" className="w-full">
                      <SelectValue placeholder="Select taxonomy type" />
                    </SelectTrigger>
                    <SelectContent>
                {TAXONOMY_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                    {type}
                        </SelectItem>
                ))}
                    </SelectContent>
              </Select>
                </div>
              </div>
            </div>
            <SheetFooter className="border-t pt-4">
            <Button
                variant="outline"
              onClick={() => {
                  setOpenEditSheet(false);
                  setUpdateWithCascade(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateException}
              disabled={!editForm.engen_id || !editForm.taxonomy_type || !selectedException}
            >
              Update
            </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogDescription>
              Are you sure you want to delete the exception with Engen ID "{selectedException?.engen_id}"?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-destructive">
                    This will delete ALL exceptions for this engen_id with cascading to related taxonomy types.
                  </p>
                  <p className="text-sm text-muted-foreground">
              This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenDeleteDialog(false)}>
              Cancel
            </Button>
              <Button variant="destructive" onClick={handleDeleteException}>
              Delete
            </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default EngenExceptions;
